<!-- The image, with the tools that point at it sitting on top of it.
  --
  -- This is the part of the single-image path that cannot be shared with a
  -- batch: you drag a rectangle across a photograph, which needs one
  -- photograph. Mounted twice -- inline on a full-sized screen, and inside
  -- CropModal on a phone, where the column is too narrow to drag in -- so
  -- there is one implementation rather than two that drift.
  --
  -- The rectangle lives in DISPLAY pixels while it is being dragged, because
  -- that is what the pointer speaks, and converts to SOURCE pixels once. Doing
  -- it per pointermove accumulates rounding until the region drifts off the
  -- thing the rulers were pointing at. The arithmetic is in lib/cropGeometry.js
  -- so it can be tested without a browser.
  --
  -- The flips are previewed here with a CSS transform, so what you see is what
  -- gets uploaded -- and the rectangle is restated back out of that mirror by
  -- mirroredRegion() before it leaves, because the pointer speaks the MIRRORED
  -- picture while everything downstream reads the original. Previewing without
  -- restating is what "a preview under a crop box would misreport which region
  -- you are choosing" means, and it was live for the flips for months.
  -- Rotation deliberately is NOT previewed: the crop rectangle is expressed in
  -- the un-rotated image. Rotate stays a parameter in the panel, where it
  -- composes with everything else.
  -->
<template>
    <div class="flex flex-col gap-3">
        <div ref="stage" class="relative flex items-center justify-center overflow-hidden rounded-xl bg-stage select-none touch-none"
             :style="{minHeight: minHeight + 'px'}">

            <div v-if="!shownSrc" class="py-16 text-[15px] text-ink-label">{{ t('crop.pickFirst') }}</div>

            <!-- data-picture-box: the box that IS the picture, and the only
              -- element on the page whose rectangle means "what the reader is
              -- looking at". WatermarkLayer walks up to it when the soft
              -- keyboard opens -- keeping the caption above the keyboard is
              -- not enough if the picture it sits on is underneath it. -->
            <div v-else data-picture-box class="relative overflow-hidden" :style="{width: frame.w + 'px', height: frame.h + 'px'}">
                <!-- Sized to the picture BEFORE the turn, then turned. A quarter
                  -- turn swaps the frame's sides, and an element that is rotated
                  -- keeps the layout box it started with -- so the picture has
                  -- to be given the unturned box and rotated inside the turned
                  -- one, or a portrait photograph at 90 degrees is drawn into a
                  -- portrait hole and cropped to a stripe. -->
                <img :src="shownSrc" alt="" draggable="false"
                     class="block absolute left-1/2 top-1/2"
                     :class="presetRatio > 0 && !cropping ? 'object-cover' : 'object-contain'"
                     :style="pictureStyle" />

                <!-- Over the picture, under the crop rectangle: the handles have
                  -- to stay visible against a photograph that may be almost
                  -- black by the time the visitor is done.
                  --
                  -- The same component the drop zone thumbnail uses, given this
                  -- surface's own box. Both show the scrim and the watermark;
                  -- neither computes them. -->
                <WatermarkLayer :w="frame.w" :h="frame.h" :baked="bakedDecorations"
                                :aspect="trueAspect" :disabled="cropping"
                                :editing="cropping ? null : editing"
                                @text="$emit('caption-text', $event)"
                                @done="$emit('caption-done')" />

                <!-- WHERE THE PICTURE WILL BE CUT, on /split-image.
                  --
                  -- Two selects would have been enough to ship and would have
                  -- been the wrong shape: "is 3 x 4 what I want" is answered
                  -- by LOOKING at where the lines fall on this particular
                  -- picture, never by reading two numbers. Same argument the
                  -- meme rectangles record, where a mode and an accept button
                  -- were replaced by dragging the thing itself.
                  --
                  -- THE LINES COME FROM tileEdges(), NOT FROM repeat(n, 1fr).
                  -- CSS would divide the box evenly and the real cut does not
                  -- -- boundaries are rounded so no pixel is lost, which makes
                  -- tiles differ by up to one. The difference is invisible at
                  -- preview scale, and a preview drawn by a second rule is
                  -- exactly what lib/meme.js exists to prevent. One function
                  -- decides, both surfaces ask it.
                  --
                  -- Hidden while cropping: two grids over one photograph, one
                  -- of them the rule-of-thirds inside the crop rectangle, is
                  -- two different things saying the same thing in one place. -->
                <div v-if="tileLines && !cropping" data-tile-overlay
                     class="pointer-events-none absolute inset-0">
                    <span v-for="(x, i) in tileLines.vertical" :key="'v' + i"
                          class="absolute top-0 bottom-0 w-px bg-white/70"
                          :style="{left: x + '%', boxShadow: '0 0 0 1px rgba(11,9,20,0.45)'}"></span>
                    <span v-for="(y, i) in tileLines.horizontal" :key="'h' + i"
                          class="absolute left-0 right-0 h-px bg-white/70"
                          :style="{top: y + '%', boxShadow: '0 0 0 1px rgba(11,9,20,0.45)'}"></span>
                </div>

                <!-- data-crop-box / data-crop-grip for the same reason the
                  -- caption rectangle below carries its own: a driver selecting
                  -- on the CLASSES drags whatever else happens to be bordered,
                  -- which cost a case once already. -->
                <div v-if="rect && cropping" data-crop-box
                     class="absolute border-2 border-white cursor-move"
                     :style="{left: rect.x + 'px', top: rect.y + 'px', width: rect.w + 'px', height: rect.h + 'px',
                              boxShadow: '0 0 0 9999px rgba(11, 9, 20, 0.55)'}"
                     @pointerdown="startDrag($event, 'move')">
                    <div class="pointer-events-none absolute inset-0 grid grid-cols-3 grid-rows-3">
                        <div v-for="n in 9" :key="n"
                             :class="['border-white/30', n % 3 !== 0 ? 'border-r' : '', n <= 6 ? 'border-b' : '']"></div>
                    </div>
                    <span v-for="h in HANDLES" :key="h" :data-crop-grip="h"
                          class="absolute block h-5 w-5 rounded-[3px] bg-white shadow"
                          :style="handleStyle(h)"
                          @pointerdown.stop="startDrag($event, h)"></span>
                    <span v-if="cropPx" class="absolute -top-8 left-0 rounded-md bg-code-bg px-2 py-1 text-xs font-bold tabular-nums text-white">
                        {{cropPx.w}} &times; {{cropPx.h}}
                    </span>
                </div>

                <!-- BOTH caption rectangles, on the picture from the moment it
                  -- is staged and dragged directly. No button opens them and
                  -- none accepts them: the words follow every move, so there is
                  -- nothing to accept.
                  --
                  -- Deliberately NO darkening outside them: a crop is a
                  -- decision about what to keep, so hiding the rest helps,
                  -- whereas a caption is a decision about where words sit ON
                  -- the picture and you have to see the picture to make it.
                  --
                  -- Dashed and brand-coloured rather than solid white, because
                  -- the captions themselves are drawn underneath and two solid
                  -- white outlines over one photograph is one too many. The
                  -- colours are Tailwind theme tokens rather than literals:
                  -- this is our furniture laid over the visitor's picture, so
                  -- it follows the theme, and tests/test_palette.py forbids a
                  -- raw hex here in any case. -->
                <div v-for="(box, slot) in captionRects" :key="slot"
                     :data-caption-box="slot"
                     :aria-label="$t('crop.captionArea', { n: areaNumber(slot) })"
                     class="absolute cursor-move rounded-[2px] border border-dashed border-brand"
                     :style="{left: box.x + 'px', top: box.y + 'px',
                              width: box.w + 'px', height: box.h + 'px'}"
                     @pointerdown="startBoxDrag($event, 'move', slot)">
                    <!-- WHAT THE EMPTY RECTANGLE IS FOR.
                      -- A dashed box with four corner grips and nothing in it
                      -- reads as a crop tool, which is the wrong guess and the
                      -- one that was reported: a converted GIF on the stage
                      -- with no sign that words are available at all.
                      --
                      -- Only while the slot is empty. Once there are words,
                      -- WatermarkLayer draws them in this same rectangle and a
                      -- hint would sit on top of them.
                      --
                      -- pointer-events-none, or the label swallows the tap that
                      -- is supposed to reach the rectangle and focus the field. -->
                    <span v-if="captionEmpty && captionEmpty[slot]" data-caption-hint
                          class="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2
                                 -translate-y-1/2 whitespace-nowrap rounded-full bg-paper
                                 px-3 py-1 text-[12px] font-bold tracking-wide text-brand shadow">
                        {{ $t('crop.captionAdd') }}
                    </span>
                    <span v-for="h in HANDLES" :key="h" :data-caption-grip="h"
                          class="absolute block h-4 w-4 rounded-[3px] bg-brand shadow"
                          :style="handleStyle(h)"
                          @pointerdown.stop="startBoxDrag($event, h, slot)"></span>
                </div>
            </div>

        </div>

        <!-- Under the picture, not on it.
          -- The bar used to float over the bottom of the image, which put it
          -- squarely on top of the two lower drag handles: they were there and
          -- draggable, and you could not see them. Nothing that sits ON the
          -- picture can avoid that, because the rectangle can be dragged
          -- anywhere on it.
          --
          -- Tools on the left, the two answers on the right, one row. -->
        <div v-if="src && tools" class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex items-center gap-1 rounded-xl bg-code-bg p-1.5">
                <button v-for="t in TOOLS" :key="t.id" @click.prevent="fire(t.id)" :data-tool="t.id"
                        :title="$t(t.titleKey)" :aria-label="$t(t.titleKey)" :aria-pressed="isOn(t.id) ? 'true' : 'false'"
                        class="flex h-11 w-11 items-center justify-center rounded-[9px] text-code-ink transition-colors"
                        :class="isOn(t.id) ? 'bg-brand-fill text-white' : 'hover:bg-white/10'">
                    <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                         stroke-linecap="round" stroke-linejoin="round" v-html="t.path"></svg>
                </button>
                <!-- Only where the turn is a directive we can actually send.
                  -- A document is rasterised on the server and keeps its own
                  -- per-page rotation, so its turn stays in the settings
                  -- panel; this row is not rendered for one anyway. -->
                <template v-if="turns">
                    <span class="mx-1 h-6 w-px bg-white/20"></span>
                    <button v-for="t in TURNS" :key="t.id" @click.prevent="$emit('turn', t.deg)"
                            :data-tool="t.id"
                            :title="$t(t.titleKey)" :aria-label="$t(t.titleKey)"
                            class="flex h-11 w-11 items-center justify-center rounded-[9px] text-code-ink transition-colors hover:bg-white/10">
                        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75"
                             stroke-linecap="round" stroke-linejoin="round" v-html="t.path"></svg>
                    </button>
                </template>
                <span class="mx-1 h-6 w-px bg-white/20"></span>
                <button @click.prevent="$emit('reset')" class="px-3 text-[13px] font-bold text-code-label hover:text-white">{{ t('crop.reset') }}</button>
            </div>

            <div v-if="cropping" class="flex flex-wrap items-center gap-2">
                <button @click.prevent="wholeImage" data-crop-whole class="btn btn-sm btn-ghost rounded-lg">{{ t('crop.wholeImage') }}</button>
                <button @click.prevent="commit" data-crop-apply class="btn btn-sm btn-primary rounded-lg" :disabled="!cropPx">{{ t('crop.useRegion') }}</button>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue"
import { moveRect, resizeRect, defaultRect, toSource, fromSource, mirroredRegion } from "../lib/cropGeometry"
import { MEME_SLOTS, memeSlots, MIN_MEME_BOX, defaultCaptionBox } from "../lib/meme"
import { tileEdges, tilesFit } from "../lib/tileSplit"
import { useI18n } from 'vue-i18n'
import WatermarkLayer from "./WatermarkLayer.vue"

const props = defineProps({
    // What we crop FROM: always the original, because cropping a crop
    // compounds the loss and cannot widen the region again.
    file: {type: Object, default: null},
    /* Which caption slot is being typed into, passed straight through to the
     * layer that draws it. Withheld while the crop tool is up: a crop
     * rectangle and a caret in the same rectangle are two gestures fighting
     * for one drag. */
    editing: {type: String, default: null},
    // What to SHOW when not cropping: the current result, so committing a
    // region visibly does something. Without it the surface went back to
    // displaying the whole original and the crop looked like it had been
    // ignored.
    display: {type: Object, default: null},
    crop: {type: Object, default: null},
    flipH: {type: Boolean, default: false},
    flipV: {type: Boolean, default: false},
    // What the DISPLAY file already contains. The two are not the same
    // question and conflating them is what made a flip appear to bounce: the
    // mirror went on instantly, the canvas pass baked the same flip into the
    // bytes a few seconds later, and the mirror was still there on top of it.
    bakedFlipH: {type: Boolean, default: false},
    bakedFlipV: {type: Boolean, default: false},
    // Whether the bytes already carry the scrim and the watermark. They do
    // between a download and the next edit, and drawing them again over the top
    // would be darker, or signed twice.
    bakedDecorations: {type: Boolean, default: false},
    // The chosen shape, as a ratio, or 0 for "keep the original". The CDN
    // crops to it on the way out; nothing moves the pixels here, so this is the
    // only place the choice can be seen before the file arrives.
    presetRatio: {type: Number, default: 0},
    // And the quarter turns, for the same reason: r_<deg> is a directive, so
    // the turn happens on the way out and the stage is the only place it can be
    // seen before the file arrives.
    rotate: {type: Number, default: 0},
    tools: {type: Boolean, default: true},
    // The two quarter turns, in the same row as the crop and the flips. Off
    // wherever r_<deg> is not the right instrument -- a clip carries its own
    // display rotation and every decoder honours it.
    turns: {type: Boolean, default: true},
    minHeight: {type: Number, default: 320},
    /* How many caption areas this picture has. A COUNT rather than a pair of
     * booleans: the areas are added in order, so "how many" is the whole
     * question, and the store holds the same number for the panel. */
    zones: {type: Number, default: 1},
    /* The caption rectangles, as {top, bottom} fractions, or null on every
     * page that has no captions.
     *
     * Null rather than an empty object on purpose: it is what decides whether
     * the two extra tools appear at all, and twelve of the fourteen task pages
     * must not grow a button for a feature they do not have.
     */
    captionBoxes: {type: Object, default: null},
    /* {cols, rows, width, height} on a page that cuts, null everywhere else.
     * The source size comes with it because the line positions are derived
     * from the real cut rather than from an even division -- see the overlay
     * above. */
    tiles: {type: Object, default: null},
    /* Which slots have no words in them yet, so the empty rectangle can say
     * what it is for. Passed in rather than read here: this component is given
     * the BOXES, and giving it the text as well would be two sources for
     * "is there a caption" one refactor apart from disagreeing. */
    captionEmpty: {type: Object, default: null},
})

const { t } = useI18n()
const emit = defineEmits(["apply-crop", "flip-h", "flip-v", "turn", "reset", "caption-box",
                          "caption-tap", "caption-text", "caption-done"])

const HANDLES = ["nw", "ne", "sw", "se"]

// Matches MIN_CROP_PX in lib/imageTransform.js, which discards anything under it
const MIN_SOURCE_PX = 8

const TOOLS = [
    {id: "crop",  titleKey: "crop.region",
     path: '<path d="M6 2v14a2 2 0 0 0 2 2h14"></path><path d="M2 6h14a2 2 0 0 1 2 2v14"></path>'},
    {id: "flipH", titleKey: "crop.flipH",
     path: '<path d="M8 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3"></path><path d="M16 3h3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-3"></path><path d="M12 2v2M12 8v2M12 14v2M12 20v2"></path>'},
    {id: "flipV", titleKey: "crop.flipV",
     path: '<path d="M21 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v3"></path><path d="M21 16v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3"></path><path d="M2 12h2M8 12h2M14 12h2M20 12h2"></path>'},
]

/* THE TURNS ARE A SECOND GROUP, and they are STEPPERS rather than toggles.
 *
 * Reported as "the task only shows the controls to crop and flip the image
 * but not the ones to rotate" -- on /rotate-image, the page whose whole
 * subject is the turn. It was in the settings panel behind "Show advanced
 * options", which is the one place somebody looking at the picture will not
 * look. The crop and the flips have been on the picture all along, and a turn
 * is the same kind of gesture: you judge it by LOOKING, which is the argument
 * SvgBackground.vue already makes for putting a control next to what it
 * changes.
 *
 * Left and right rather than 0/90/180/270, because that is what the task's own
 * copy has always promised -- "quarter turns in either direction", and the FAQ
 * says "left, right, or a half turn". Two presses the same way IS the half
 * turn, so nothing is lost and there is no fourth button that undoes itself.
 *
 * They do not light up, because there is no state to show: a stepper that
 * looks pressed is a toggle that lies. Which way up the picture is, is on the
 * picture. */
const TURNS = [
    {id: "turnLeft", deg: -90, titleKey: "crop.turnLeft",
     path: '<path d="M9 14 4 9l5-5"></path><path d="M4 9h10a6 6 0 0 1 6 6v0a6 6 0 0 1-6 6H8"></path>'},
    {id: "turnRight", deg: 90, titleKey: "crop.turnRight",
     path: '<path d="m15 14 5-5-5-5"></path><path d="M20 9H10a6 6 0 0 0-6 6v0a6 6 0 0 0 6 6h6"></path>'},
]

const src = ref("")
const natural = reactive({w: 0, h: 0})
const shown = reactive({w: 0, h: 0})
/* The cut lines, as percentages of the picture box.
 *
 * Percentages rather than pixels because the box is whatever the stage could
 * give it and changes with the window, while the cut is a fact about the
 * SOURCE. Derived from tileEdges() -- the same function that decides the real
 * tiles -- so the preview cannot drift from the file.
 *
 * The outer edges are dropped: a line on the frame's own border is the frame,
 * and drawing it there makes a 1x1 "grid" look like a cut when nothing is
 * being cut.
 */
const tileLines = computed(() => {
    const t = props.tiles
    if (!t || !(t.width > 0 && t.height > 0)) {
        return null
    }
    const cols = Number(t.cols) || 0
    const rows = Number(t.rows) || 0
    if (!tilesFit(t.width, t.height, cols, rows)) {
        return null
    }
    const inner = (edges, total) =>
        edges.slice(1, -1).map((v) => (v / total) * 100)
    return {
        vertical: inner(tileEdges(t.width, cols), t.width),
        horizontal: inner(tileEdges(t.height, rows), t.height)
    }
})

const rect = ref(null)
const cropping = ref(false)
/* The caption rectangles are NOT held here.
 *
 * They are on the picture from the moment it is staged and are dragged
 * directly, so their position is read straight out of the store on every frame
 * and written straight back during a drag. Holding a copy would mean a
 * rectangle that has to be pushed one way and pulled the other, and a text
 * layout that follows the copy rather than the thing that will be drawn -- and
 * that is exactly what the first attempt got wrong: it applied the rectangle
 * only when a button was pressed, so nothing moved while anybody dragged.
 *
 * They ARE in a different coordinate space from the crop rectangle, which is
 * why they are not sharing its `rect` either: `rect` is measured against
 * `shown`, the ORIGINAL fitted to the stage, because a crop region has to be
 * expressed in the original. A caption sits on the picture as it is NOW --
 * `frame` -- and after a crop those are two different images.
 */
const stage = ref(null)

let objectUrl = ""          // the source: what the rectangle is measured against
// A ref, not a plain let. shownSrc below is a computed, and a computed cannot
// depend on an ordinary variable: assigning to one changes nothing on screen.
// That is exactly how the first version of this failed -- the crop was applied,
// uploaded and re-rendered, and the surface went on showing the original.
const shownUrl = ref("")    // what is on screen when not cropping
let drag = null
let ro = null

function releaseUrl() {
    if (objectUrl) { URL.revokeObjectURL(objectUrl); objectUrl = "" }
}
function releaseShown() {
    if (shownUrl.value) { URL.revokeObjectURL(shownUrl.value); shownUrl.value = "" }
}
onBeforeUnmount(() => { releaseUrl(); releaseShown(); endDrag(); if (ro) { ro.disconnect(); ro = null } })

/* Measure the stage whenever it changes size, not only when the image loads.
 *
 * fit() used to run from the load handler alone. The image can load while this
 * surface is behind a tab that is not showing, and then the stage has no width:
 * the Math.max(80, ...) floor turns that into an 80px-wide picture, and nothing
 * ever recomputed it. A five-thousand-pixel photograph came out drawn at 80x53
 * in the corner of an empty panel with its handles piled on top of each other.
 *
 * The rectangle is kept across the re-fit by converting it to source pixels and
 * back, because it is stored in DISPLAY pixels -- re-fitting without that would
 * throw away whatever region the visitor had already dragged.
 */
function remeasure() {
    if (!natural.w) return
    const before = {w: shown.w, h: shown.h}
    const keep = (rect.value && before.w) ? toSource(rect.value, before, natural, 1) : null
    fit()
    if (!shown.w || (shown.w === before.w && shown.h === before.h)) return
    rect.value = keep ? fromSource(keep, shown, natural) : defaultRect(shown)
    // Nothing to do for the caption rectangles: they are derived from stored
    // FRACTIONS on every frame, and a fraction is already independent of the
    // box it is drawn in.
}

onMounted(() => {
    if (!stage.value || typeof ResizeObserver === "undefined") return
    ro = new ResizeObserver(remeasure)
    ro.observe(stage.value)
})

/* The rectangle's coordinate space comes from the SOURCE, measured off-DOM.
 *
 * It used to come from the rendered <img>'s load event, which meant the element
 * on screen and the coordinate space were the same thing -- so showing the
 * cropped result would have silently redefined "natural" as the crop's own
 * dimensions and every subsequent region would have been wrong. Decoding the
 * source separately lets the surface display one image and measure another. */
watch(() => props.file, async (f) => {
    releaseUrl()
    if (!f) { src.value = ""; natural.w = 0; natural.h = 0; return }
    objectUrl = URL.createObjectURL(f)
    src.value = objectUrl
    const probe = new Image()
    probe.src = objectUrl
    try {
        await probe.decode()
    } catch (e) {
        return                       // a source we cannot decode has no geometry
    }
    natural.w = probe.naturalWidth
    natural.h = probe.naturalHeight
    fit()
    rect.value = props.crop
        ? fromSource(mirroredRegion(props.crop, natural, props.flipH, props.flipV), shown, natural)
        : defaultRect(shown)
    await nextTick()
}, {immediate: true})

const displayNatural = reactive({w: 0, h: 0})

watch(() => props.display, async (f) => {
    releaseShown()
    displayNatural.w = 0
    displayNatural.h = 0
    if (!f) return
    shownUrl.value = URL.createObjectURL(f)
    const probe = new Image()
    probe.src = shownUrl.value
    try {
        await probe.decode()
    } catch (e) {
        return
    }
    displayNatural.w = probe.naturalWidth
    displayNatural.h = probe.naturalHeight
}, {immediate: true})

/* The box on screen. While cropping it is `shown`, because that is the space
 * the rectangle's numbers are in. Otherwise it takes the shape of whatever is
 * being displayed, so a cropped result is not letterboxed inside a frame cut
 * for the original. */
/* A quarter turn, which swaps the frame's sides. Half a turn does not.
 *
 * Cropping is exempt, like the chosen shape: the rectangle is dragged over the
 * untouched picture, and turning it under the rectangle would put the region
 * somewhere else. */
const turn = computed(() => (cropping.value ? 0 : ((props.rotate % 360) + 360) % 360))
const quarterTurn = computed(() => turn.value === 90 || turn.value === 270)

/* The box the picture is drawn into, before any turn. */
const unturned = computed(() => {
    if (cropping.value || !displayNatural.w || !shown.w) return shown

    // A chosen shape overrides the picture's own. The CDN will crop to it with
    // m_cropbox, which fills the frame and loses the overflow rather than
    // letterboxing -- object-fit: cover, drawn below, is that same rule.
    // Cropping is exempt: the rectangle has to sit on the whole picture.
    const ratio = props.presetRatio > 0 ? props.presetRatio
                                        : displayNatural.w / displayNatural.h
    const byW = {w: shown.w, h: shown.w / ratio}
    const byH = {w: shown.h * ratio, h: shown.h}
    const fit = byW.h <= shown.h ? byW : byH
    // Never enlarged past the picture's own size when it keeps its own shape,
    // which is what the old Math.min(..., 1) said.
    const cap = props.presetRatio > 0
        ? 1
        : Math.min(shown.w / displayNatural.w, shown.h / displayNatural.h, 1)
    if (props.presetRatio > 0) return {w: Math.round(fit.w), h: Math.round(fit.h)}
    return {w: Math.round(displayNatural.w * cap), h: Math.round(displayNatural.h * cap)}
})

/* The picture's real shape, before it was rounded into a box of whole pixels.
 *
 * frame.w and frame.h are what the picture occupies on screen and are rounded,
 * so their ratio is a near miss: a 1600x1067 photograph fitted into a 336-wide
 * box measures 336x224, a ratio of 1.5000 where the truth is 1.4995. Every mark
 * except the captions is placed off the SHORT edge and does not care. A caption
 * wraps, and the canvas works the wrapping out from the unrounded shape, so
 * handing the preview the rounded one is how the two could break a line in
 * different places. Zero while nothing has decoded, which the prop treats as
 * "use the box".
 */
const trueAspect = computed(() => (displayNatural.w && displayNatural.h)
    ? displayNatural.w / displayNatural.h
    : 0)

/* What the surface reserves: the unturned box, on its side for a quarter turn,
 * and shrunk if standing it on end would push it past the space available. */
const frame = computed(() => {
    const u = unturned.value
    if (!quarterTurn.value) return u
    const s = Math.min(1, shown.w ? shown.w / u.h : 1)
    return {w: Math.round(u.h * s), h: Math.round(u.w * s)}
})

/* The picture keeps the unturned box and is turned inside the frame, because a
 * rotated element keeps the layout box it started with. */
const pictureStyle = computed(() => {
    const u = unturned.value
    const s = quarterTurn.value && shown.w ? Math.min(1, shown.w / u.h) : 1
    const parts = [
        "translate(-50%, -50%)",
        turn.value ? `rotate(${turn.value}deg)` : "",
        `scale(${(mirrorH.value ? -1 : 1) * s}, ${(mirrorV.value ? -1 : 1) * s})`,
    ]
    return {
        width: u.w + "px",
        height: u.h + "px",
        transform: parts.filter(Boolean).join(" "),
    }
})

/* Mirror the picture only by the amount the bytes do not already carry.
 *
 * While cropping, what is on screen is the untouched original, so the whole
 * flip has to be drawn by CSS or the rectangle would sit on a picture the
 * wrong way round. Otherwise the display file has been through the canvas, and
 * only a flip asked for since then is still owed a mirror -- usually none, and
 * briefly one while the pass runs, which is what makes the control feel
 * immediate.
 */
const mirrorH = computed(() => cropping.value ? props.flipH : props.flipH !== props.bakedFlipH)
const mirrorV = computed(() => cropping.value ? props.flipV : props.flipV !== props.bakedFlipV)

/* Cropping: the original, because that is what the rectangle sits on. Not
 * cropping: the current result, so a committed region is visible. */
const shownSrc = computed(() => {
    if (cropping.value) return src.value
    return shownUrl.value || src.value
})

// A new crop arriving from elsewhere (the panel clearing it, say) has to be
// reflected here rather than left showing the old rectangle.
watch(() => props.crop, (c) => {
    if (!natural.w) return
    rect.value = c
        ? fromSource(mirroredRegion(c, natural, props.flipH, props.flipV), shown, natural)
        : defaultRect(shown)
})



function fit() {
    const box = stage.value ? stage.value.getBoundingClientRect() : {width: 480, height: props.minHeight}
    const avail = {w: Math.max(80, box.width - 24), h: Math.max(120, Math.max(box.height, props.minHeight) - 24)}
    const s = Math.min(avail.w / natural.w, avail.h / natural.h, 1)
    shown.w = Math.round(natural.w * s)
    shown.h = Math.round(natural.h * s)
}

const scale = computed(() => (natural.w && shown.w) ? natural.w / shown.w : 1)
/* The rectangle, in the ORIGINAL's own frame.
 *
 * Mirrored back out of the frame it was dragged in: while cropping the picture
 * on screen is the original with a CSS flip over it, so the pointer speaks the
 * MIRRORED picture and everything downstream reads the unmirrored one. See
 * mirroredRegion() for what that costs when it is skipped. */
const cropPx = computed(() => mirroredRegion(
    toSource(rect.value, shown, natural, MIN_SOURCE_PX), natural, props.flipH, props.flipV))

function isOn(id) {
    if (id === "crop") return cropping.value
    if (id === "flipH") return props.flipH
    if (id === "flipV") return props.flipV
    return false
}

function fire(id) {
    if (id === "crop") { cropping.value = !cropping.value; return }
    emit(id === "flipH" ? "flip-h" : "flip-v")
}

/* Where each caption's rectangle is, in frame pixels, for drawing the handles.
 *
 * Derived, never stored. A slot with no rectangle of its own gets the band
 * lib/meme.js would have used anyway -- MEME_MARGIN in from the edges,
 * MEME_MAX_BLOCK tall -- so the handles start around the words rather than
 * somewhere the visitor has to go and find. Because captionTops() anchors to
 * the rectangle's near edge, picking one up and putting it straight back down
 * moves nothing.
 */
const captionRects = computed(() => {
    const f = frame.value
    const out = {}
    if (!props.captionBoxes || !f.w || !f.h || cropping.value) return out
    const short = Math.min(f.w, f.h)
    const shape = trueAspect.value > 0 ? trueAspect.value : f.w / f.h
    for (const slot of memeSlots(props.zones)) {
        const stored = props.captionBoxes[slot]
        if (stored) {
            out[slot] = {
                x: Math.round(stored.x * f.w), y: Math.round(stored.y * f.h),
                w: Math.round(stored.w * f.w), h: Math.round(stored.h * f.h),
            }
            continue
        }
        // From lib/meme.js, not worked out again here. The handles have to
        // start exactly where the words already are, and the words are placed
        // from this same function -- two versions of "the default band" would
        // move the caption the first time anybody touched its rectangle.
        const band = defaultCaptionBox(slot, shape)
        out[slot] = {
            x: Math.round(band.x * short), y: Math.round(band.y * short),
            w: Math.round(band.w * short), h: Math.round(band.h * short),
        }
    }
    return out
})

/* What the reader calls this area: its position in the list, one-based.
 *
 * The slot names describe the band each area OPENS in, not where it stays, so
 * "the top caption" stopped being true the day the rectangles became
 * draggable -- and on a three-label meme it is actively wrong. The accessible
 * name is the same Area 1..4 the panel's fields carry. */
function areaNumber(slot) {
    return MEME_SLOTS.indexOf(slot) + 1
}

function startBoxDrag(ev, mode, slot) {
    const from = captionRects.value[slot]
    if (!from) return
    ev.preventDefault()
    ev.stopPropagation()
    // Guarded, unlike the crop's equivalent: setPointerCapture throws
    // NotFoundError when the pointerId matches no active pointer, which is
    // exactly what a synthetic event has. Capture is a nicety -- the move and
    // up listeners are on the window either way -- so losing it must not cost
    // the drag. It is also what makes this drivable from tests/e2e_test.sh.
    try {
        if (ev.currentTarget.setPointerCapture) ev.currentTarget.setPointerCapture(ev.pointerId)
    } catch (err) { /* no captured pointer; the window listeners still fire */ }
    drag = {mode, startX: ev.clientX, startY: ev.clientY, from: {...from},
            target: ev.currentTarget, id: ev.pointerId, slot, moved: false}
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", endDrag)
    window.addEventListener("pointercancel", endDrag)
}

/* The rectangle, as FRACTIONS of the picture.
 *
 * Not pixels: the same rectangle has to mean the same thing to the stage
 * preview, to the canvas that bakes it, and to a picture that has been scaled
 * on the way out. usableMemeBox() in lib/imageTransform.js clamps and
 * sanity-checks whatever arrives.
 *
 * `settled` is false while the pointer is still down. The words follow every
 * move, which costs a repaint and nothing else because the preview is CSS;
 * re-encoding the photograph is left until the drag ends. It is the same split
 * the sliders use -- write the store on input, ask for the work on change.
 */
function sendBox(slot, r, settled) {
    const f = frame.value
    if (!slot || !r || !f.w || !f.h) return
    emit("caption-box", {
        slot, settled,
        box: {x: r.x / f.w, y: r.y / f.h, w: r.w / f.w, h: r.h / f.h},
    })
}

function handleStyle(h) {
    return {
        top:    h[0] === "n" ? "-10px" : "auto",
        bottom: h[0] === "s" ? "-10px" : "auto",
        left:   h[1] === "w" ? "-10px" : "auto",
        right:  h[1] === "e" ? "-10px" : "auto",
        cursor: (h === "nw" || h === "se") ? "nwse-resize" : "nesw-resize",
    }
}

function startDrag(ev, mode) {
    if (!rect.value) return
    ev.preventDefault()
    if (ev.currentTarget.setPointerCapture) ev.currentTarget.setPointerCapture(ev.pointerId)
    drag = {mode, startX: ev.clientX, startY: ev.clientY, from: {...rect.value},
            target: ev.currentTarget, id: ev.pointerId}
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", endDrag)
    window.addEventListener("pointercancel", endDrag)
}

/* Anything under this is a tap with a shaky thumb, not a drag. */
const TAP_SLOP_PX = 6

function onMove(ev) {
    if (!drag) return
    const dx = ev.clientX - drag.startX
    const dy = ev.clientY - drag.startY
    if (Math.abs(dx) > TAP_SLOP_PX || Math.abs(dy) > TAP_SLOP_PX) drag.moved = true
    // A caption rectangle is bounded by the picture as it is NOW; a crop region
    // by the original it is a region of. Same arithmetic, different rectangle.
    const bounds = drag.slot ? {w: frame.value.w, h: frame.value.h} : {w: shown.w, h: shown.h}
    // the source minimum expressed on screen, but never smaller than a
    // fingertip can reliably land on
    const min = drag.slot
        ? Math.max(24, Math.ceil(Math.min(bounds.w, bounds.h) * MIN_MEME_BOX))
        : Math.max(12, Math.ceil(MIN_SOURCE_PX / scale.value))
    const next = drag.mode === "move"
        ? moveRect(drag.from, dx, dy, bounds)
        : resizeRect(drag.mode, drag.from, dx, dy, bounds, min)
    if (drag.slot) sendBox(drag.slot, next, false)
    else rect.value = next
}

function endDrag() {
    if (drag && drag.target && drag.target.releasePointerCapture) {
        try { drag.target.releasePointerCapture(drag.id) } catch (err) { /* already released */ }
    }
    // The pointer is up: now the bytes may be brought into line with what the
    // stage has been showing all along.
    if (drag && drag.slot) {
        sendBox(drag.slot, captionRects.value[drag.slot], true)
        /* A TAP, not a drag: down and up without travelling.
         *
         * On a phone the field that fills this rectangle is below the stage,
         * the button and the whole options panel, so tapping Area 1 to write
         * in it appears to do nothing. The distinction matters both ways --
         * emitting on every pointerup would scroll the page out from under
         * somebody who was only moving the box. */
        if (!drag.moved) emit("caption-tap", drag.slot)
    }
    drag = null
    window.removeEventListener("pointermove", onMove)
    window.removeEventListener("pointerup", endDrag)
    window.removeEventListener("pointercancel", endDrag)
}

function wholeImage() {
    cropping.value = false
    rect.value = defaultRect(shown)
    emit("apply-crop", null)
}

function commit() {
    if (!cropPx.value) return
    const c = cropPx.value
    const whole = c.x === 0 && c.y === 0 && c.w === natural.w && c.h === natural.h
    cropping.value = false
    emit("apply-crop", whole ? null : c)
}

defineExpose({openCrop: () => { cropping.value = true }})
</script>
